<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="visible" class="blm-overlay" @click.self="close">
        <div class="blm-modal">
          <div class="blm-header">
            <h2 class="blm-title">
              <i class="fas fa-building me-2"></i>Sucursales de Correo Argentino
            </h2>
            <button class="blm-close" @click="close">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="blm-search">
            <div class="row g-2 align-items-end">
              <div class="col-12 col-sm-5">
                <label class="blm-label">
                  <i class="fas fa-map-marker-alt me-1"></i>Código Postal
                </label>
                <div class="input-group">
                  <input
                    ref="postalInput"
                    v-model="postalCode"
                    type="text"
                    class="form-control blm-input"
                    placeholder="Ej: 1650 o B1650BFG"
                    maxlength="8"
                    @keyup.enter="searchByPostalCode"
                  />
                  <button
                    class="btn blm-search-btn"
                    :disabled="loading || !postalCode || postalCode.length < 4"
                    @click="searchByPostalCode"
                  >
                    <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                    <template v-else><i class="fas fa-search"></i></template>
                  </button>
                </div>
              </div>
              <div class="col-12 col-sm-4">
                <label class="blm-label">
                  <i class="fas fa-map me-1"></i>O provincia
                </label>
                <select
                  v-model="selectedProvince"
                  class="form-select blm-select"
                  :disabled="loading"
                  @change="onProvinceChange"
                >
                  <option value="">Elegir...</option>
                  <option v-for="p in provinces" :key="p.code" :value="p.code">{{ p.name }}</option>
                </select>
              </div>
              <div class="col-12 col-sm-3">
                <label class="blm-label">
                  <i class="fas fa-filter me-1"></i>Filtrar
                </label>
                <input
                  v-model="searchQuery"
                  type="text"
                  class="form-control blm-input"
                  placeholder="Nombre..."
                  :disabled="!agencies.length"
                />
              </div>
            </div>

            <div v-if="hasSearched && !loading && !errorMsg" class="blm-results-bar mt-2">
              <span v-if="resolvedProvinceName" class="blm-province-tag">
                <i class="fas fa-map-pin me-1"></i>{{ resolvedProvinceName }}
              </span>
              <span class="blm-count">
                {{ filteredAgencies.length }} sucursal{{ filteredAgencies.length !== 1 ? 'es' : '' }}
                <template v-if="nearbyCount > 0"> · {{ nearbyCount }} cercana{{ nearbyCount !== 1 ? 's' : '' }}</template>
              </span>
            </div>
          </div>

          <div class="blm-body">
            <div v-if="loading" class="blm-state">
              <i class="fas fa-spinner fa-spin fa-2x mb-2"></i>
              <p>Buscando sucursales...</p>
            </div>

            <div v-else-if="errorMsg" class="blm-state blm-state--error">
              <i class="fas fa-exclamation-triangle fa-2x mb-2"></i>
              <p>{{ errorMsg }}</p>
              <button class="btn btn-sm blm-retry-btn" @click="retrySearch">
                <i class="fas fa-redo me-1"></i>Reintentar
              </button>
            </div>

            <div v-else-if="hasSearched && agencies.length === 0" class="blm-state">
              <i class="fas fa-inbox fa-2x mb-2" style="color: var(--warning-color);"></i>
              <p>No se encontraron sucursales.</p>
            </div>

            <div v-else-if="!hasSearched" class="blm-state">
              <i class="fas fa-map-marked-alt fa-2x mb-2" style="color: var(--primary-color);"></i>
              <p>Ingresá tu código postal para buscar sucursales cercanas.</p>
            </div>

            <div v-show="showResults" class="blm-content">
              <div class="blm-map-wrap">
                <div ref="mapContainer" class="blm-map"></div>
              </div>

              <div class="blm-list">
                <div
                  v-for="agency in filteredAgencies"
                  :key="agency.code"
                  class="blm-agency"
                  :class="{
                    'blm-agency--active': selectedAgency?.code === agency.code,
                    'blm-agency--nearby': isNearbyAgency(agency)
                  }"
                  @click="selectAgency(agency)"
                >
                  <div class="blm-agency-icon">
                    <i class="fas fa-building"></i>
                  </div>

                  <div class="blm-agency-info">
                    <div class="blm-agency-name-row">
                      <strong>{{ agency.name }}</strong>
                      <span v-if="isNearbyAgency(agency)" class="blm-nearby-tag">
                        <i class="fas fa-location-arrow me-1"></i>Cercana
                      </span>
                    </div>

                    <small class="blm-detail">
                      <i class="fas fa-map-marker-alt me-1"></i>{{ formatAddress(agency) }}
                    </small>

                    <small v-if="getLocality(agency)" class="blm-detail">
                      <i class="fas fa-city me-1"></i>{{ getLocality(agency) }}
                    </small>

                    <small v-if="agency.phone" class="blm-detail">
                      <i class="fas fa-phone me-1"></i>{{ agency.phone }}
                    </small>

                    <small class="blm-detail blm-hours-highlight">
                      <i class="fas fa-clock me-1"></i>{{ getTodayHours(agency) }}
                    </small>
                  </div>

                  <div class="blm-agency-arrow">
                    <i class="fas fa-chevron-right"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedAgency" class="blm-footer">
            <div class="blm-footer-selected">
              <i class="fas fa-building me-1"></i>
              <strong>{{ selectedAgency.name }}</strong>
              <small class="ms-2 text-muted">{{ formatAddress(selectedAgency) }}</small>
            </div>
            <div class="blm-footer-actions">
              <label class="blm-save-check" @click.stop>
                <input type="checkbox" v-model="saveForNext" />
                <span>Utilizar esta sucursal para próximos envíos</span>
              </label>
              <button class="btn blm-confirm-btn" @click="confirmSelection">
                <i class="fas fa-check me-1"></i>Seleccionar sucursal
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { getAgencies, savePreferredBranch } from '@/services/shipping';
import { useStore } from 'vuex';
import L from 'leaflet';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// eslint-disable-next-line no-undef
const props = defineProps({
  modelValue: { type: Boolean, default: false },
  initialPostalCode: { type: String, default: '' },
});
// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue', 'branch-selected']);

const store = useStore();
const isAuthenticated = computed(() => !!store.getters?.isAuthenticated || !!localStorage.getItem('accessToken'));

const visible = computed(() => props.modelValue);

const close = () => emit('update:modelValue', false);

const postalInput = ref(null);
const mapContainer = ref(null);
const loading = ref(false);
const errorMsg = ref('');
const postalCode = ref('');
const selectedProvince = ref('');
const searchQuery = ref('');
const provinces = ref([]);
const agencies = ref([]);
const selectedAgency = ref(null);
const hasSearched = ref(false);
const searchedPostalCode = ref('');
const resolvedProvinceName = ref('');

const showResults = computed(() =>
  !loading.value && !errorMsg.value && hasSearched.value && agencies.value.length > 0
);

let map = null;
let markersGroup = null;

const PROVINCES_FALLBACK = [
  { code: 'A', name: 'Salta' }, { code: 'B', name: 'Buenos Aires' }, { code: 'C', name: 'CABA' },
  { code: 'D', name: 'San Luis' }, { code: 'E', name: 'Entre Ríos' }, { code: 'F', name: 'La Rioja' },
  { code: 'G', name: 'Santiago del Estero' }, { code: 'H', name: 'Chaco' }, { code: 'J', name: 'San Juan' },
  { code: 'K', name: 'Catamarca' }, { code: 'L', name: 'La Pampa' }, { code: 'M', name: 'Mendoza' },
  { code: 'N', name: 'Misiones' }, { code: 'P', name: 'Formosa' }, { code: 'Q', name: 'Neuquén' },
  { code: 'R', name: 'Río Negro' }, { code: 'S', name: 'Santa Fe' }, { code: 'T', name: 'Tucumán' },
  { code: 'U', name: 'Chubut' }, { code: 'V', name: 'Tierra del Fuego' }, { code: 'W', name: 'Corrientes' },
  { code: 'X', name: 'Córdoba' }, { code: 'Y', name: 'Jujuy' }, { code: 'Z', name: 'Santa Cruz' },
].sort((a, b) => a.name.localeCompare(b.name));

const PROVINCE_COORDS = {
  'A': [-24.7859, -65.4117], 'B': [-36.6769, -60.5588], 'C': [-34.6037, -58.3816],
  'D': [-33.3017, -66.3378], 'E': [-31.7413, -60.1556], 'F': [-29.4131, -66.8559],
  'G': [-27.7834, -64.2642], 'H': [-27.4534, -59.0186], 'J': [-31.5375, -68.5364],
  'K': [-28.4696, -65.7852], 'L': [-36.6167, -64.2833], 'M': [-32.8895, -68.8458],
  'N': [-27.3621, -55.8969], 'P': [-26.1775, -58.1781], 'Q': [-38.9516, -68.0591],
  'R': [-40.8135, -63.0], 'S': [-31.6333, -60.7], 'T': [-26.8241, -65.2226],
  'U': [-43.3002, -65.1023], 'V': [-54.8019, -68.3030], 'W': [-27.4693, -58.8306],
  'X': [-31.4201, -64.1888], 'Y': [-24.1858, -65.2995], 'Z': [-51.6230, -69.2168],
};

const agencyIcon = L.divIcon({
  html: '<div class="custom-marker"><i class="fas fa-building"></i></div>',
  className: 'custom-marker-container', iconSize: [22, 22], iconAnchor: [16, 22], popupAnchor: [0, -22],
});
const nearbyIcon = L.divIcon({
  html: '<div class="custom-marker custom-marker--nearby"><i class="fas fa-star"></i></div>',
  className: 'custom-marker-container', iconSize: [24, 24], iconAnchor: [17, 24], popupAnchor: [0, -24],
});
const selectedIconMarker = L.divIcon({
  html: '<div class="custom-marker custom-marker--selected"><i class="fas fa-building"></i></div>',
  className: 'custom-marker-container', iconSize: [26, 26], iconAnchor: [18, 26], popupAnchor: [0, -26],
});

const extractNumericCP = (input) => {
  if (!input) return '';
  const clean = input.trim();
  if (clean[0] && /[A-Za-z]/.test(clean[0])) return clean.slice(1).replace(/[^0-9]/g, '').slice(0, 4);
  return clean.replace(/[^0-9]/g, '').slice(0, 4);
};

const isNearbyAgency = (agency) => {
  if (!searchedPostalCode.value) return false;
  const userCP = extractNumericCP(searchedPostalCode.value);
  if (!userCP) return false;
  const nbcp = agency.nearByPostalCode;
  if (!nbcp) return false;
  return nbcp.split(',').map(c => c.trim()).includes(userCP);
};

const formatAddress = (agency) => {
  const addr = agency.location?.address;
  if (!addr) return 'Dirección no disponible';
  return [addr.streetName, addr.streetNumber].filter(Boolean).join(' ') || 'Dirección no disponible';
};

const getLocality = (agency) => {
  const addr = agency.location?.address;
  if (!addr) return '';
  return [addr.locality, addr.province].filter(Boolean).join(', ');
};

const formatHour = (t) => (!t || t.length < 4) ? '' : `${t.slice(0, 2)}:${t.slice(2, 4)}`;

const getTodayHours = (agency) => {
  if (!agency.hours) return 'Horario no disponible';
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const h = agency.hours[days[new Date().getDay()]];
  return h ? `Hoy ${formatHour(h.start)} - ${formatHour(h.end)}` : 'Cerrado hoy';
};

const getAgencyCoords = (agency) => {
  const lat = parseFloat(agency.location?.latitude);
  const lng = parseFloat(agency.location?.longitude);
  return (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) ? { lat, lng } : null;
};

const formatScheduleHtml = (agency) => {
  if (!agency.hours) return '';
  const labels = { monday: 'Lun', tuesday: 'Mar', wednesday: 'Mié', thursday: 'Jue', friday: 'Vie', saturday: 'Sáb', sunday: 'Dom' };
  return Object.entries(labels).filter(([d]) => agency.hours[d]).map(([d, l]) => `${l}: ${formatHour(agency.hours[d].start)} - ${formatHour(agency.hours[d].end)}`).join('<br>');
};

const nearbyCount = computed(() => agencies.value.filter(isNearbyAgency).length);

const filteredAgencies = computed(() => {
  let list = [...agencies.value];
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(a => {
      return [a.name, formatAddress(a), a.location?.address?.locality, a.location?.address?.city, a.location?.address?.postalCode, a.nearByPostalCode]
        .filter(Boolean).some(s => s.toLowerCase().includes(q));
    });
  }
  list.sort((a, b) => {
    const d = (isNearbyAgency(a) ? 0 : 1) - (isNearbyAgency(b) ? 0 : 1);
    return d !== 0 ? d : (a.name || '').localeCompare(b.name || '');
  });
  return list;
});

const destroyMap = () => {
  if (map) {
    try { map.remove(); } catch { /* */ }
    map = null;
    markersGroup = null;
  }
};

const initMap = () => {
  if (!mapContainer.value) return;

  if (map) {
    try {
      map.invalidateSize();
      return;
    } catch {
      destroyMap();
    }
  }

  map = L.map(mapContainer.value, { center: [-38.4161, -63.6167], zoom: 4, zoomControl: true, scrollWheelZoom: true });
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
  markersGroup = L.featureGroup().addTo(map);

  setTimeout(() => { if (map) map.invalidateSize(); }, 100);
  setTimeout(() => { if (map) map.invalidateSize(); }, 300);
  setTimeout(() => { if (map) map.invalidateSize(); }, 600);
};

const updateMapMarkers = () => {
  if (!map || !markersGroup) return;
  markersGroup.clearLayers();
  let hasCoords = false;

  filteredAgencies.value.forEach((agency) => {
    const coords = getAgencyCoords(agency);
    if (!coords) return;
    hasCoords = true;

    const isSel = selectedAgency.value?.code === agency.code;
    const isNear = isNearbyAgency(agency);
    const icon = isSel ? selectedIconMarker : isNear ? nearbyIcon : agencyIcon;

    const addr = formatAddress(agency);
    const locality = getLocality(agency);
    const schedule = formatScheduleHtml(agency);

    const marker = L.marker([coords.lat, coords.lng], { icon });
    marker.bindPopup(`
      <div style="font-family:sans-serif;min-width:180px;">
        <strong style="font-size:0.85rem;">${agency.name}</strong>
        ${isNear ? '<span style="background:rgba(var(--primary-color-rgb), 0.2);color:var(--primary-color);padding:1px 5px;border-radius:4px;font-size:0.6rem;margin-left:4px;">Cercana</span>' : ''}
        ${addr ? `<br><small style="color:#666;"><i class="fas fa-map-marker-alt"></i> ${addr}</small>` : ''}
        ${locality ? `<br><small style="color:#666;"><i class="fas fa-city"></i> ${locality}</small>` : ''}
        ${agency.phone ? `<br><small style="color:#666;"><i class="fas fa-phone"></i> ${agency.phone}</small>` : ''}
        ${schedule ? `<br><div style="margin-top:3px;padding-top:3px;border-top:1px solid #eee;"><small style="color:#888;">${schedule}</small></div>` : ''}
      </div>
    `);
    marker.on('click', () => { selectedAgency.value = agency; });
    markersGroup.addLayer(marker);
  });

  if (hasCoords && markersGroup.getLayers().length > 0) {
    nextTick(() => { try { map.fitBounds(markersGroup.getBounds().pad(0.1)); } catch { /* */ } });
  } else if (selectedProvince.value && PROVINCE_COORDS[selectedProvince.value]) {
    map.setView(PROVINCE_COORDS[selectedProvince.value], 10);
  }
};

const saveForNext = ref(false);
const savingBranch = ref(false);

const selectAgency = (agency) => {
  selectedAgency.value = agency;
  const coords = getAgencyCoords(agency);
  if (coords && map) {
    map.setView([coords.lat, coords.lng], 15, { animate: true });
    updateMapMarkers();
    markersGroup.eachLayer((layer) => {
      const ll = layer.getLatLng();
      if (Math.abs(ll.lat - coords.lat) < 0.0001 && Math.abs(ll.lng - coords.lng) < 0.0001) layer.openPopup();
    });
  }
};

const confirmSelection = async () => {
  if (!selectedAgency.value) return;

  const agency = selectedAgency.value;
  const addr = agency.location?.address;
  const agencyName = [
    agency.name,
    addr ? [addr.streetName, addr.streetNumber].filter(Boolean).join(' ') : '',
    addr?.city || addr?.locality || '',
  ].filter(Boolean).join(' — ');

  if (saveForNext.value && isAuthenticated.value) {
    savingBranch.value = true;
    try {
      await savePreferredBranch(agency.code, agencyName);
    } catch (err) {
      console.error('Error saving preferred branch:', err);
    } finally {
      savingBranch.value = false;
    }
  }

  emit('branch-selected', {
    code: agency.code,
    name: agency.name,
    fullName: agencyName,
    phone: agency.phone || '',
    address: addr ? [addr.streetName, addr.streetNumber].filter(Boolean).join(' ') : '',
    city: addr?.city || addr?.locality || '',
    province: addr?.province || '',
    provinceCode: addr?.provinceCode || '',
    postalCode: addr?.postalCode || '',
    savedForNext: saveForNext.value,
  });

  close();
};

const fetchAgencies = async (params) => {
  loading.value = true;
  errorMsg.value = '';
  agencies.value = [];
  selectedAgency.value = null;
  searchQuery.value = '';

  try {
    const result = await getAgencies(params);
    agencies.value = result.agencies || [];
    if (result.provinces?.length) provinces.value = result.provinces;
    if (result.resolved_province) selectedProvince.value = result.resolved_province;
    if (result.resolved_province_name) resolvedProvinceName.value = result.resolved_province_name;
    if (result.searched_postal_code) searchedPostalCode.value = result.searched_postal_code;
    hasSearched.value = true;
  } catch (err) {
    console.error('Error fetching agencies:', err);
    errorMsg.value = err.response?.data?.detail || err.response?.data?.error || 'No pudimos cargar las sucursales. Intentá nuevamente.';
    if (err.response?.data?.provinces) provinces.value = err.response.data.provinces;
  } finally {
    loading.value = false;
  }
};

const searchByPostalCode = () => {
  if (!postalCode.value || postalCode.value.length < 4) return;
  fetchAgencies({ postalCode: postalCode.value });
};

const onProvinceChange = () => {
  if (!selectedProvince.value) return;
  searchedPostalCode.value = '';
  resolvedProvinceName.value = provinces.value.find(p => p.code === selectedProvince.value)?.name || '';
  fetchAgencies({ provinceCode: selectedProvince.value });
};

const retrySearch = () => {
  if (postalCode.value?.length >= 4) searchByPostalCode();
  else if (selectedProvince.value) onProvinceChange();
};

const loadProvinces = async () => {
  try {
    const result = await getAgencies();
    if (result.provinces?.length) provinces.value = result.provinces;
  } catch { provinces.value = PROVINCES_FALLBACK; }
};

watch(filteredAgencies, () => { if (map) updateMapMarkers(); });

watch(showResults, async (show) => {
  if (show) {
    await nextTick();
    destroyMap();
    await nextTick();
    initMap();
    await nextTick();
    updateMapMarkers();
    setTimeout(() => { if (map) { map.invalidateSize(); updateMapMarkers(); } }, 400);
  }
});

watch(visible, async (isOpen) => {
  if (isOpen) {
    if (props.initialPostalCode && !postalCode.value) {
      postalCode.value = props.initialPostalCode;
    }
    await loadProvinces();
    await nextTick();
    postalInput.value?.focus();
    if (postalCode.value && postalCode.value.length >= 4 && !hasSearched.value) {
      searchByPostalCode();
    }
  } else {
    destroyMap();
  }
});

const onKeydown = (e) => { if (e.key === 'Escape' && visible.value) close(); };
onMounted(() => document.addEventListener('keydown', onKeydown));
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
  destroyMap();
});
</script>

<style>
@import 'leaflet/dist/leaflet.css';

.custom-marker-container { background: none !important; border: none !important; }
.custom-marker {
  width: 32px; height: 32px; border-radius: 50% 50% 50% 0;
  background: var(--primary-color);
  display: flex; align-items: center; justify-content: center;
  transform: rotate(-45deg); box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
.custom-marker i { transform: rotate(45deg); color: #fff; font-size: 0.7rem; }
.custom-marker--nearby { background: var(--warning-color); box-shadow: 0 2px 10px rgba(245,158,11,0.5); }
.custom-marker--selected {
  background: var(--primary-dark); width: 36px; height: 36px;
  box-shadow: 0 2px 12px rgba(var(--primary-color-rgb), 0.5);
}
.custom-marker--selected i { font-size: 0.8rem; }

.leaflet-popup-content-wrapper { background: #fff !important; color: #1a1a1a !important; border-radius: 10px !important; box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important; }
.leaflet-popup-tip { background: #fff !important; }
.leaflet-popup-content { color: #1a1a1a !important; margin: 10px 14px !important; }
.leaflet-popup-close-button { color: #666 !important; }
</style>

<style scoped>
.blm-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.blm-modal {
  background: #fff;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  width: 100%;
  max-width: 960px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-xl);
}

.modal-fade-enter-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.modal-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.modal-fade-enter-from { opacity: 0; transform: scale(0.95); }
.modal-fade-leave-to { opacity: 0; transform: scale(0.95); }

.blm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-color);
}

.blm-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.blm-title i { color: var(--primary-color); }

.blm-close {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  width: 34px; height: 34px;
  border-radius: var(--radius-sm);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
}
.blm-close:hover { background: var(--hover-bg); color: var(--text-primary); }

.blm-search {
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid var(--border-light);
}

.blm-label {
  color: var(--text-muted);
  font-size: 0.7rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  display: block;
}

.blm-label i { color: var(--primary-color); }

.blm-input, .blm-select {
  background: #fff;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  border-radius: var(--radius-sm);
  font-size: 0.82rem;
  padding: 0.45rem 0.7rem;
}

.blm-input:focus, .blm-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(var(--primary-color-rgb), 0.15);
}

.blm-search-btn {
  background: var(--primary-color);
  color: #fff !important;
  border: none;
  padding: 0.45rem 0.85rem;
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}
.blm-search-btn:hover:not(:disabled) { background: var(--primary-dark); color: #fff !important; }
.blm-search-btn:disabled { background: rgba(var(--primary-color-rgb), 0.4); color: rgba(255,255,255,0.7) !important; }

.blm-results-bar { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
.blm-province-tag {
  background: rgba(var(--primary-color-rgb), 0.12);
  color: var(--primary-color);
  padding: 0.15rem 0.55rem;
  border-radius: 20px;
  font-size: 0.68rem;
  font-weight: 600;
}
.blm-count { color: var(--text-muted); font-size: 0.72rem; }

.blm-body {
  flex: 1;
  overflow-y: auto;
}

.blm-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
}

.blm-state--error { color: var(--danger-color); }

.blm-retry-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  font-size: 0.75rem;
}
.blm-retry-btn:hover { background: var(--hover-bg); color: var(--text-primary); }

.blm-map { height: 280px; width: 100%; }

.blm-list {
  max-height: 320px;
  overflow-y: auto;
}

.blm-agency {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.8rem 1.25rem;
  border-bottom: 1px solid var(--border-light);
  cursor: pointer;
}
.blm-agency:hover { background: var(--hover-bg); }
.blm-agency--active { background: rgba(var(--primary-color-rgb), 0.08); border-left: 3px solid var(--primary-color); }
.blm-agency--nearby { border-left: 3px solid var(--warning-color); }

.blm-agency-icon {
  width: 30px; height: 30px; min-width: 30px;
  border-radius: var(--radius-sm);
  background: rgba(var(--primary-color-rgb), 0.1);
  color: var(--primary-color);
  display: flex; align-items: center; justify-content: center;
  font-size: 0.7rem;
}

.blm-agency-info { flex: 1; min-width: 0; }
.blm-agency-name-row { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; }
.blm-agency-name-row strong { font-size: 0.78rem; color: var(--text-primary); }
.blm-nearby-tag {
  background: rgba(245, 158, 11, 0.15); color: var(--warning-color);
  padding: 1px 5px; border-radius: 4px; font-size: 0.55rem; font-weight: 600;
}
.blm-detail { color: var(--text-muted); font-size: 0.68rem; line-height: 1.4; }
.blm-hours-highlight { color: var(--primary-color); font-weight: 500; }
.blm-agency-arrow { color: var(--text-muted); font-size: 0.6rem; }

.blm-footer {
  border-top: 1px solid var(--border-color);
  padding: 0.75rem 1.25rem;
  background: rgba(var(--primary-color-rgb), 0.04);
}

.blm-footer-selected {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  font-size: 0.78rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}
.blm-footer-selected i { color: var(--primary-color); }

.blm-footer-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.blm-save-check {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.72rem;
  color: var(--text-muted);
}
.blm-save-check input[type="checkbox"] {
  width: 15px; height: 15px;
  accent-color: var(--primary-color);
  cursor: pointer;
}

.blm-confirm-btn {
  background: var(--primary-color);
  color: #fff !important;
  border: none;
  border-radius: var(--radius-sm);
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
}
.blm-confirm-btn:hover { background: var(--primary-dark); color: #fff !important; }

@media (max-width: 768px) {
  .blm-modal { max-height: 95vh; }
  .blm-map { height: 200px; }
  .blm-list { max-height: 250px; }
  .blm-footer-actions { flex-direction: column; align-items: stretch; }
  .blm-confirm-btn { width: 100%; }
}
</style>
